/**
 * ADR-70 cut 6f (hitlist 10 and 11): the Online interest panel, in the
 * clerk's words.
 *
 * The ring sends this screen alone an INTEREST frame when what it shows
 * changes: how many are watching, which approved bidders have the sale open,
 * and who is about to do what on the lot in the ring. Nothing here is
 * recorded, and nothing here is an amount (hitlist 10.2: never typed
 * amounts). A Super Admin's switch decides whether there is a panel at all,
 * and each of its controls decides which part.
 *
 * Pure, so the words are testable without a browser or a ring.
 */

import type { InterestFrame } from '@hipnumber/protocol-client';
import type { BidderReg } from './ring-history.js';
import { maxBidderWords } from './max-bids.js';

type Signal = InterestFrame['signals'][number];
type Bidder = NonNullable<InterestFrame['present']>[number];

/** Hitlist 10.2: subtle for a pointer on Bid, stronger for a confirmation
 *  open, a flash for a bid just pressed. */
export type InterestTone = 'hover' | 'confirm' | 'submitted';

export interface InterestLine {
  /** Stable across frames, so a line that is still there is not re-flashed. */
  key: string;
  who: string;
  what: string;
  tone: InterestTone;
}

export interface InterestView {
  /** "34 watching · 8 bidders present", each part only when shown. */
  summary: string;
  lines: InterestLine[];
  /** Who is present, in the clerk's words, longest connected first. */
  present: string[] | null;
  /** Present but not named here (past the list's length or the ring's cap). */
  presentMore: number;
  /** Said when no bidder is doing anything on the lot. */
  empty: string | null;
}

const DEVICE: Readonly<Record<string, string>> = { MOBILE: 'phone', TABLET: 'tablet', DESKTOP: 'computer' };

const WHAT: Readonly<Record<Signal['kind'], { what: string; tone: InterestTone }>> = {
  BID_SUBMITTED: { what: 'bid sent', tone: 'submitted' },
  BID_CONFIRM_OPEN: { what: 'confirming a bid', tone: 'confirm' },
  OUTBID_CONFIRM_OPEN: { what: 'outbid, confirming a raise', tone: 'confirm' },
  BID_BUTTON_HOVERED: { what: 'on the Bid button', tone: 'hover' },
};

const plural = (n: number, one: string, many: string): string => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** A bidder as the panel names them: paddle and name when identity is on,
 *  else the ring's number for them ("Online bidder 3"); the device after. */
export function interestWho(b: Bidder, regs: readonly BidderReg[]): string {
  const who = b.bidderId ? maxBidderWords(b.bidderId, regs) : `Online bidder ${b.handle}`;
  const device = b.device ? DEVICE[b.device] : undefined;
  return device ? `${who} (${device})` : who;
}

/** What the panel shows of one INTEREST frame, for the lot now in the ring.
 *  Signals about another lot (the frame is older than the lot change) are
 *  not shown: the next frame says what stands. */
export function interestView(f: InterestFrame, ringLotId: string | null, regs: readonly BidderReg[], maxPresent = 12): InterestView {
  const parts: string[] = [];
  if (f.watching !== null) parts.push(`${f.watching.toLocaleString('en-US')} watching`);
  if (f.biddersPresent !== null) parts.push(plural(f.biddersPresent, 'bidder present', 'bidders present'));
  const current = f.lotId !== null && f.lotId === ringLotId;
  const lines = current
    ? f.signals.map((s): InterestLine => ({
        key: `${s.handle}|${s.kind}|${s.since}`,
        who: interestWho(s, regs),
        ...WHAT[s.kind],
      }))
    : [];
  const shown = f.present?.slice(0, maxPresent) ?? null;
  return {
    summary: parts.join(' · '),
    lines,
    present: shown ? shown.map((b) => interestWho(b, regs)) : null,
    presentMore: f.biddersPresent !== null && shown ? Math.max(0, f.biddersPresent - shown.length) : 0,
    empty: lines.length ? null : ringLotId === null ? 'No lot in the ring.' : 'No bidder activity on this lot right now.',
  };
}
